import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Sparkles, AlertCircle, Clock } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

const STATE_META = {
  trialing: { label: "Trial", color: "bg-amber-100 text-amber-800 ring-amber-200" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800 ring-emerald-200" },
  past_due: { label: "Past due", color: "bg-red-100 text-red-800 ring-red-200" },
  paywall: { label: "Locked", color: "bg-slate-200 text-slate-800 ring-slate-300" },
};

export default function BillingSection() {
  const { billing, refresh } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [portalBusy, setPortalBusy] = useState(false);

  // Handle Checkout success return — Stripe webhook may still be catching up,
  // so poll the billing status a few times.
  useEffect(() => {
    const billingParam = params.get("billing");
    if (!billingParam) return;
    if (billingParam === "success") {
      toast.success("Thanks! Payment processed — activating your subscription…");
      let attempts = 0;
      const tick = async () => {
        attempts++;
        await refresh();
        if (attempts < 6) setTimeout(tick, 1500);
      };
      tick();
    } else if (billingParam === "canceled") {
      toast.message("Checkout canceled — you can pick a plan any time.");
    }
    params.delete("billing");
    params.delete("session_id");
    setParams(params, { replace: true });
  }, []);

  if (!billing) return null;

  const meta = STATE_META[billing.state] || STATE_META.paywall;
  const isSubscribed = billing.state === "active" || billing.state === "past_due";
  const showBrowsePlans = !isSubscribed;
  const plan = billing.plan;

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", {
        origin_url: window.location.origin,
      });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPortalBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="settings-billing-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-1.5">
            <CreditCard className="h-3.5 w-3.5" /> Billing
          </div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <span
              className={`inline-flex items-center rounded-full text-xs font-semibold px-2 py-0.5 ring-1 ${meta.color}`}
              data-testid="billing-state-chip"
            >
              {meta.label}
            </span>
            {plan && (
              <span className="text-sm text-slate-900 font-semibold" data-testid="billing-plan-label">
                {plan.label}
                <span className="ml-1 text-slate-500 font-normal">
                  · €{plan.amount_eur.toLocaleString()}/{plan.interval === "year" ? "yr" : "mo"}
                </span>
              </span>
            )}
            {plan?.seats_max && (
              <span className="text-xs text-slate-500" data-testid="billing-seats">
                up to {plan.seats_max} CRM users
              </span>
            )}
          </div>

          <div className="mt-3 text-sm text-slate-600 space-y-1">
            {billing.state === "trialing" && (
              <div className="flex items-center gap-2" data-testid="billing-trial-info">
                <Clock className="h-3.5 w-3.5 text-slate-400" />
                <span>
                  Free trial ends{" "}
                  <span className="font-semibold text-slate-900">
                    {formatDate(billing.trial_ends_at)}
                  </span>{" "}
                  ({billing.trial_days_left} day{billing.trial_days_left === 1 ? "" : "s"} left)
                </span>
              </div>
            )}
            {billing.current_period_end && isSubscribed && (
              <div data-testid="billing-renewal">
                {billing.cancel_at_period_end
                  ? "Access ends "
                  : "Renews on "}
                <span className="font-semibold text-slate-900">
                  {formatDate(billing.current_period_end)}
                </span>
              </div>
            )}
            {billing.payment_failed && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-red-800" data-testid="billing-failed-notice">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Your last payment failed. Update your card in the billing portal to keep access.
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {showBrowsePlans && (
            <button
              onClick={() => nav("/billing/upgrade")}
              data-testid="settings-choose-plan-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              <Sparkles className="h-4 w-4" /> Choose a plan
            </button>
          )}
          {isSubscribed && (
            <button
              onClick={openPortal}
              disabled={portalBusy}
              data-testid="settings-manage-billing-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white px-4 py-2 text-sm font-semibold hover:bg-slate-800 transition-colors disabled:opacity-60"
            >
              <ExternalLink className="h-4 w-4" />
              {portalBusy ? "Opening…" : "Manage billing"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
